import React, { createContext, useContext, useMemo, useEffect, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useCurrentWorkspace } from 'src/context/reducers/app-settings';
import { useGetCompetitiveIntelligenceKpisQuery } from 'src/context/api/competitorsMonitoring/api';
import { fDate } from 'src/utils/format-time';
import countriesJson from 'src/utils/countries.json';
import { CountryItem } from '../types';

// Context pour partager les données communes entre tous les composants
interface CompetitiveIntelligenceContextValue {
    // Données de base
    competitiveIntelligenceData: any[];
    isLoadingBaseData: boolean;
    brandTrafficShareByName: Map<string, number>;
    
    // Données traitées des pays
    countriesData: CountryItem[];
    countryLevelByName: Map<string, string>;
    selectedCountry: string | null;
    setSelectedCountry: (country: string | null) => void;
    
    // Helper functions
    skipQuery: boolean;
    workspace: any;
    filterStore: any;
}

export const CompetitiveIntelligenceContext = createContext<CompetitiveIntelligenceContextValue | null>(null);

// Provider centralisé qui fetche une seule fois les données communes
export const CompetitiveIntelligenceProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    // Vérifier si un context parent existe déjà pour éviter les doublons
    const existingContext = useContext(CompetitiveIntelligenceContext);
    
    // Si un provider parent existe déjà, on ne fait que passer les children sans créer un nouveau provider
    if (existingContext) {
        return <>{children}</>;
    }
    
    const workspace: any = useCurrentWorkspace();
    const filterStore = useSelector((state: any) => state.filters);
    
    const skipQuery = !filterStore.start || !filterStore.end || !workspace?.id;

    // Fetch unique des données de competitive intelligence
    const { data: competitiveIntelligenceData, isFetching: isLoadingBaseData } = useGetCompetitiveIntelligenceKpisQuery({
        startDate: filterStore.start !== undefined ? fDate(filterStore.start, "yyyy-MM-dd") : null,
        endDate: filterStore.end !== undefined ? fDate(filterStore.end, "yyyy-MM-dd") : null,
        compareStartDate: filterStore.compareStart !== undefined ? fDate(filterStore.compareStart, "yyyy-MM-dd") : null,
        compareEndDate: filterStore.compareEnd !== undefined ? fDate(filterStore.compareEnd, "yyyy-MM-dd") : null,
        timestep: filterStore.timestep,
        devices: filterStore.devices,
        countries: filterStore.countries,
        keywords: filterStore.keywords,
        platforms: filterStore?.platforms,
        workspaceId: workspace?.id,
    }, {
        skip: skipQuery,
    });

    // Traitement centralisé des données pays
    const ciItems: any[] = useMemo(() => 
        Array.isArray(competitiveIntelligenceData) ? competitiveIntelligenceData : [], 
        [competitiveIntelligenceData]
    );
    
    const countriesList = useMemo(() => {
        const uniqueCountryNames = Array.from(new Set(ciItems.map((it: any) => it?.country_name).filter(Boolean)));
        return uniqueCountryNames.map((name: string) => {
            const found = countriesJson?.find((c: any) => c.name === name);
            return { country_name: name, country_code: found ? found.code : name?.slice(0, 2).toLowerCase() };
        });
    }, [ciItems]);

    const countryLevelByName = useMemo(() => {
        const severityRank: Record<string, number> = { low: 0, active: 1, high: 2 };
        const levels = new Map<string, string>();
        for (const item of ciItems) {
            const countryName: string | undefined = (item?.country_name ?? item?.country) as string | undefined;
            const level: string | undefined = (item?.level ?? item?.abs_level ?? item?.severity_level) as string | undefined;
            if (!countryName || !level) continue;
            const current = levels.get(countryName);
            if (!current || (severityRank[level] ?? -1) > (severityRank[current] ?? -1)) {
                levels.set(countryName, level);
            }
        }
        return levels;
    }, [ciItems]);

    const countriesData: CountryItem[] = useMemo(() => {
        return countriesList.map(country => ({
            ...country,
            level: countryLevelByName.get(country.country_name)
        }));
    }, [countriesList, countryLevelByName]);

    // Map brand traffic share by country name
    const brandTrafficShareByName = useMemo(() => {
        const map = new Map<string, number>();
        for (const item of ciItems) {
            const countryName = item?.country_name;
            const share = item?.brand_traffic_share;
            if (countryName != null && typeof share === 'number') {
                map.set(countryName, share);
            }
        }
        return map;
    }, [ciItems]);

    // Gestion du pays sélectionné (persistant + deep link)
    const [selectedCountry, setSelectedCountryState] = useState<string | null>(null);
    const initializedRef = useRef<boolean>(false);

    // Applique la sélection et persiste
    const setSelectedCountry = (country: string | null) => {
        setSelectedCountryState(country);
        try {
            if (workspace?.id) {
                const key = `ci.selectedCountry.${workspace.id}`;
                if (country) localStorage.setItem(key, country);
                else localStorage.removeItem(key);
            }
        } catch {}
    };

    // Initialisation depuis deep link (?country=FR) ou localStorage, puis fallback premier pays
    useEffect(() => {
        if (!workspace?.id) return;
        if (initializedRef.current) return;
        // Ne pas initialiser tant qu'on n'a pas la liste des pays
        if (!countriesData || countriesData.length === 0) return;

        let initial: string | null = null;
        try {
            const params = new URLSearchParams(window.location.search);
            const codeParam = params.get('country');
            if (codeParam) {
                const nameFromCode = countriesJson?.find((c: any) => String(c.code).toUpperCase() === String(codeParam).toUpperCase())?.name;
                if (nameFromCode && countriesData.some((c) => c.country_name === nameFromCode)) {
                    initial = nameFromCode;
                }
            }
        } catch {}
        if (!initial) {
            try {
                const key = `ci.selectedCountry.${workspace.id}`;
                const fromStorage = localStorage.getItem(key);
                if (fromStorage && countriesData.some((c) => c.country_name === fromStorage)) {
                    initial = fromStorage;
                }
            } catch {}
        }
        if (!initial) {
            initial = countriesData[0]?.country_name ?? null;
        }
        initializedRef.current = true;
        setSelectedCountry(initial);
    }, [countriesData, workspace?.id]);

    const contextValue: CompetitiveIntelligenceContextValue = {
        competitiveIntelligenceData: ciItems,
        isLoadingBaseData,
        brandTrafficShareByName,
        countriesData,
        countryLevelByName,
        selectedCountry,
        setSelectedCountry,
        skipQuery,
        workspace,
        filterStore,
    };

    return (
        <CompetitiveIntelligenceContext.Provider value={contextValue}>
            {children}
        </CompetitiveIntelligenceContext.Provider>
    );
};

// Hook pour consommer le contexte
export const useCompetitiveIntelligenceContext = () => {
    const context = useContext(CompetitiveIntelligenceContext);
    if (!context) {
        throw new Error('useCompetitiveIntelligenceContext must be used within CompetitiveIntelligenceProvider');
    }
    return context;
};

// Wrappers déplacés vers WithProviderWrappers.tsx pour éviter les cycles d'import